// src/features/auth/ResetPassword.jsx
//
// Step 3 of the OTP-gated password reset flow.
// Reads { email, reset_token } from React Router navigate state (never from URL).
// Sends POST /auth/reset-password → on success, redirects to /login with toast.
//
// Security: reset_token is NEVER in the URL — it's passed via history state
// and never logged, indexed, or visible in the browser address bar or Referer header.

import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate }  from 'react-router-dom';
import { Eye, EyeOff, Lock, CheckCircle } from 'lucide-react';
import { motion, AnimatePresence }         from 'framer-motion';
import { toast }    from 'sonner';
import { Button }   from '@/shared/components/ui/button';
import { Input }    from '@/shared/components/ui/input';
import { Label }    from '@/shared/components/ui/label';
import { authAPI }  from '@/api/auth.api';

// ─── Password-strength rules (must match backend's validate_password_strength) ─

const RULES = [
  { id: 'length', label: 'At least 8 characters',     test: (p) => p.length >= 8 },
  { id: 'upper',  label: 'One uppercase letter (A–Z)', test: (p) => /[A-Z]/.test(p) },
  { id: 'lower',  label: 'One lowercase letter (a–z)', test: (p) => /[a-z]/.test(p) },
  { id: 'digit',  label: 'One number (0–9)',           test: (p) => /\d/.test(p) },
];

function StrengthRule({ met, label }) {
  return (
    <li className={`flex items-center gap-1.5 text-xs transition-colors ${met ? 'text-green-600' : 'text-gray-400'}`}>
      <span className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center flex-shrink-0 transition-colors
        ${met ? 'border-green-500 bg-green-500' : 'border-gray-300'}`}
      >
        {met && <span className="text-white text-[9px] leading-none">✓</span>}
      </span>
      {label}
    </li>
  );
}

// ─── Views ────────────────────────────────────────────────────────────────────

const VIEW = {
  FORM:          'form',
  SUCCESS:       'success',
  INVALID_TOKEN: 'invalid_token',
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function ResetPassword() {
  const location = useLocation();
  const navigate  = useNavigate();

  // reset_token and email are passed via navigate state — NEVER in the URL
  const { email = '', reset_token: resetToken = '' } = location.state || {};

  const [view,            setView]            = useState(resetToken ? VIEW.FORM : VIEW.INVALID_TOKEN);
  const [newPassword,     setNewPassword]     = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew,         setShowNew]         = useState(false);
  const [showConfirm,     setShowConfirm]     = useState(false);
  const [error,           setError]           = useState('');
  const [isSubmitting,    setIsSubmitting]    = useState(false);

  // If the token is absent (e.g. direct navigation), show invalid state
  useEffect(() => {
    if (!resetToken) setView(VIEW.INVALID_TOKEN);
  }, [resetToken]);

  const rulesMet     = RULES.map((r) => r.test(newPassword));
  const allRulesMet  = rulesMet.every(Boolean);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;

  const validate = () => {
    if (!allRulesMet)    return 'Password does not meet all requirements.';
    if (!passwordsMatch) return 'Passwords do not match.';
    return '';
  };

  // ── Submit ──────────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const validationError = validate();
    if (validationError) { setError(validationError); return; }

    setIsSubmitting(true);
    try {
      await authAPI.resetPassword({
        email,
        reset_token: resetToken,
        new_password: newPassword,
      });

      setView(VIEW.SUCCESS);

      // Navigate to login after a short delay so the user reads the message
      setTimeout(() => {
        navigate('/login', { replace: true });
        toast.success('Password reset! Please sign in with your new password.');
      }, 2200);

    } catch (err) {
      const status  = err?.response?.status;
      const message = err?.response?.data?.detail || '';

      if (status === 401) {
        // Token invalid/expired/already used — send user back to start
        setError(
          message.includes('already been used')
            ? 'This reset link has already been used. Please request a new one.'
            : 'Your reset session has expired. Please start over.',
        );
      } else if (status === 422) {
        setError(message || 'Password does not meet requirements.');
      } else if (status === 429) {
        setError('Too many requests. Please wait a moment and try again.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Shared left panel ────────────────────────────────────────────────────

  const LeftPanel = () => (
    <div className="hidden lg:block lg:w-1/2 relative">
      <img
        src="https://images.unsplash.com/photo-1602173574767-37ac01994b2a?w=1200&q=80"
        alt="Gawher Ali Jewellery"
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-black/45" />
      <div className="absolute inset-0 flex items-center justify-center text-white text-center p-8">
        <div>
          <h2 className="font-playfair text-4xl mb-4">Gawher Ali Jewellery</h2>
          <p className="text-lg opacity-90">Handcrafted jewellery — premium quality at honest prices</p>
        </div>
      </div>
    </div>
  );

  // ── Render: invalid token ────────────────────────────────────────────────

  if (view === VIEW.INVALID_TOKEN) {
    return (
      <div className="min-h-screen flex">
        <LeftPanel />
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md text-center"
          >
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <span className="text-2xl">🔗</span>
            </div>
            <h1 className="font-playfair text-2xl mb-3">Invalid or expired link</h1>
            <p className="text-gray-500 text-sm leading-relaxed mb-8">
              This password reset link is invalid or has expired.
              Please request a new one.
            </p>
            <Link to="/forgot-password">
              <Button className="w-full btn-gold mb-4">Request a new reset code</Button>
            </Link>
            <Link to="/login" className="block text-sm text-antique-gold hover:underline font-medium">
              Back to Sign In
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  // ── Render: success ──────────────────────────────────────────────────────

  if (view === VIEW.SUCCESS) {
    return (
      <div className="min-h-screen flex">
        <LeftPanel />
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md text-center"
          >
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="font-playfair text-3xl mb-3">Password updated!</h1>
            <p className="text-gray-500 text-sm leading-relaxed">
              Your password has been reset successfully. You'll be redirected
              to the sign-in page in a moment.
            </p>
          </motion.div>
        </div>
      </div>
    );
  }

  // ── Render: form ─────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen flex">
      <LeftPanel />

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <AnimatePresence mode="wait">
          <motion.div
            key="reset-form"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="w-full max-w-md"
          >
            {/* Heading */}
            <div className="mb-8">
              <div className="w-12 h-12 rounded-full bg-antique-gold/10 flex items-center justify-center mb-4">
                <Lock className="w-6 h-6 text-antique-gold" />
              </div>
              <h1 className="font-playfair text-3xl mb-2">Choose a new password</h1>
              <p className="text-gray-500 text-sm">
                Setting a new password for{' '}
                <span className="font-medium text-gray-700">{email}</span>.
              </p>
            </div>

            {/* Error */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-5 p-3.5 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700"
              >
                {error}
                {(error.includes('expired') || error.includes('already been used')) && (
                  <Link
                    to="/forgot-password"
                    className="block mt-2 text-antique-gold hover:underline font-medium"
                  >
                    Request a new reset code →
                  </Link>
                )}
              </motion.div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5" noValidate>

              {/* New password */}
              <div>
                <Label htmlFor="newPassword">New Password</Label>
                <div className="relative mt-2">
                  <Input
                    id="newPassword"
                    type={showNew ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => { setNewPassword(e.target.value); if (error) setError(''); }}
                    placeholder="Enter new password"
                    autoComplete="new-password"
                    autoFocus
                    disabled={isSubmitting}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label={showNew ? 'Hide password' : 'Show password'}
                  >
                    {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>

                {/* Strength rules */}
                {newPassword.length > 0 && (
                  <ul className="mt-3 space-y-1.5 pl-1">
                    {RULES.map((r, i) => (
                      <StrengthRule key={r.id} met={rulesMet[i]} label={r.label} />
                    ))}
                  </ul>
                )}
              </div>

              {/* Confirm password */}
              <div>
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative mt-2">
                  <Input
                    id="confirmPassword"
                    type={showConfirm ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); if (error) setError(''); }}
                    placeholder="Repeat new password"
                    autoComplete="new-password"
                    disabled={isSubmitting}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label={showConfirm ? 'Hide password' : 'Show password'}
                  >
                    {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {confirmPassword.length > 0 && (
                  <p className={`mt-1.5 text-xs ${passwordsMatch ? 'text-green-600' : 'text-red-500'}`}>
                    {passwordsMatch ? '✓ Passwords match' : '✗ Passwords do not match'}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                disabled={!allRulesMet || !passwordsMatch || isSubmitting}
                className="w-full btn-gold"
              >
                {isSubmitting ? (
                  <span className="animate-pulse">Resetting password…</span>
                ) : (
                  'Reset Password'
                )}
              </Button>
            </form>

            <p className="mt-6 text-center text-sm text-gray-400">
              <Link to="/forgot-password" className="text-antique-gold hover:underline">
                ← Start over
              </Link>
            </p>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}